// 26 §3.18 (ADM-050 to ADM-054, ADM-091) and 06 CLK-131 to CLK-137: the disaster closure switch (decision record
// 0296). One read, ops.admin_closures(): the closures, newest first, with their courses, host clubs and history; a
// preview, ops.admin_closure_preview(), that changes nothing; and the commands closure.close_area, closure.review and
// closure.reopen, each with the step-up code and a reason. The server checks everything again: who may close (an
// owner, or the backup contact while away mode is on), the typed area name, the note's words, and that only an
// owner reviews or reopens. Nothing here closes or reopens anything by itself.
import type { SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { verifyCode } from '../auth/mfa';
import { adminText } from '../lib/copy';
import { codeOf, messageFor, runCommand, type CommandResult } from '../lib/commands';

/** CLK-131: the ten kinds, in 06's order. The tool keeps no list of its own beyond these. */
export const EVENT_KINDS = [
  'flood',
  'storm',
  'hurricane',
  'wildfire',
  'earthquake',
  'volcano',
  'tsunami',
  'extreme_heat',
  'extreme_cold',
  'other',
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export interface ClosureCourse {
  course_id: string;
  name: string;
  reopened_at: string | null;
  host_club: string | null;
}

export interface Closure {
  id: string;
  status: 'active' | 'lifted';
  area_name: string;
  part_of_area: boolean;
  zone_kind: 'areas' | 'circle' | 'polygon';
  area_codes: string[] | null;
  radius_m: number | null;
  event_kind: EventKind;
  tropical_word: string | null;
  note_text: string;
  note_text_under18: string;
  note_template_key: string;
  note_template_version: number;
  internal_reason: string;
  what_checked: string;
  started_at: string;
  lifted_at: string | null;
  review_due_at: string;
  review_due: boolean;
  review_overdue: boolean;
  closed_by_backup: boolean;
  courses: ClosureCourse[] | null;
  history: { at: string; command: string; result: string; reason: string | null }[] | null;
}

export interface ClosuresData {
  ok: true;
  role: 'owner' | 'backup_contact';
  sensitivity_pause: boolean;
  templates: { key: string; version: number; body: string }[];
  areas: { code: string; name_key: string; kind: string; hub: string }[];
  closures: Closure[];
}

export interface Preview {
  ok: boolean;
  error?: string;
  courses_count?: number;
  courses?: { course_id: string; name: string }[];
  clubs?: string[];
  tropical_word?: string | null;
  note_text?: string;
  note_text_under18?: string;
  note_problems?: string[];
  may_edit_note?: boolean;
}

/** What the console could load: the page, the refusal code for a role that may not use it, or a failure. */
export async function loadClosures(sb: SupabaseClient): Promise<ClosuresData | string> {
  const { data, error } = await sb.schema('ops').rpc('admin_closures');
  if (error) return codeOf(error.message);
  return data as ClosuresData;
}

const when = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/London',
});
const at = (iso: string | null) => (iso ? when.format(new Date(iso)) : '');

/** The refusals the closure commands and the preview give, each with its own words in `closures.error`. */
const CLOSURE_ERRORS = new Set([
  'forbidden',
  'event_kind_required',
  'area_name_required',
  'unknown_area',
  'bad_shape',
  'no_courses_in_zone',
  'bad_tropical_word',
  'unknown_template',
  'area_name_mismatch',
  'what_checked_required',
  'backup_cannot_edit_note',
  'note_refused',
  'closure_not_found',
  'closure_lifted',
  'not_in_this_closure',
]);

/** Plain words for a refusal of the closure commands, or the console's usual words. */
export function closureMessage(r: CommandResult): string {
  return !r.ok && CLOSURE_ERRORS.has(r.error ?? '')
    ? adminText(`closures.error.${r.error}`)
    : messageFor(r);
}

/** The zone as the close command and the preview take it. */
export function zoneArgs(z: {
  kind: 'areas' | 'circle' | 'polygon';
  areas: string[];
  lat: string;
  lon: string;
  radiusKm: string;
  geojson: string;
}): Record<string, unknown> {
  if (z.kind === 'areas') return { zone_kind: 'areas', area_codes: z.areas };
  if (z.kind === 'circle')
    return {
      zone_kind: 'circle',
      lat: Number(z.lat),
      lon: Number(z.lon),
      radius_m: Math.round(Number(z.radiusKm) * 1000),
    };
  return { zone_kind: 'polygon', geojson: z.geojson };
}

function CodeField({
  id,
  code,
  setCode,
}: {
  id: string;
  code: string;
  setCode: (s: string) => void;
}) {
  return (
    <>
      <label htmlFor={id}>{adminText('closures.code_label')}</label>
      <input
        id={id}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />
    </>
  );
}

/** Runs a command with the step-up code when the server asked for one. */
function useCommand(sb: SupabaseClient) {
  const [needCode, setNeedCode] = useState(false);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const run = async (command: string, args: Record<string, unknown>, reason: string) => {
    if (needCode) {
      if (!(await verifyCode(sb, code.trim()))) {
        setMsg(adminText('closures.code_wrong'));
        return null;
      }
      await sb.auth.refreshSession();
    }
    const r = await runCommand(sb, command, args, reason);
    setNeedCode(r.error === 'step_up_required');
    setMsg(closureMessage(r));
    return r;
  };
  return { needCode, code, setCode, msg, setMsg, run };
}

function NewClosure({
  sb,
  data,
  onDone,
}: {
  sb: SupabaseClient;
  data: ClosuresData;
  onDone: (text: string) => void;
}) {
  const [kind, setKind] = useState<'areas' | 'circle' | 'polygon'>('areas');
  const [areas, setAreas] = useState<string[]>([]);
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [radiusKm, setRadiusKm] = useState('');
  const [geojson, setGeojson] = useState('');
  const [areaName, setAreaName] = useState('');
  const [part, setPart] = useState(false);
  const [eventKind, setEventKind] = useState<EventKind | ''>('');
  const [tropical, setTropical] = useState('');
  const [template, setTemplate] = useState(
    data.templates[0] ? `${data.templates[0].key}@${data.templates[0].version}` : '',
  );
  const [preview, setPreview] = useState<Preview | null>(null);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState('');
  const [typed, setTyped] = useState('');
  const cmd = useCommand(sb);
  const [key, version] = template.split('@');
  const args = () => ({
    ...zoneArgs({ kind, areas, lat, lon, radiusKm, geojson }),
    area_name: areaName.trim(),
    part_of_area: part,
    event_kind: eventKind === '' ? null : eventKind,
    ...(eventKind === 'hurricane' && tropical ? { tropical_word: tropical } : {}),
    note_template_key: key,
    note_template_version: Number(version),
  });
  const look = async () => {
    const { data: p, error } = await sb
      .schema('ops')
      .rpc('admin_closure_preview', { p_args: args() });
    if (error) return setPreview({ ok: false, error: codeOf(error.message) });
    const pv = p as Preview;
    setPreview(pv);
    if (pv.ok) {
      setNote(pv.note_text ?? '');
      if (eventKind === 'hurricane' && !tropical) setTropical(pv.tropical_word ?? '');
    }
  };
  const missing = [
    !preview?.ok && adminText('closures.needs_preview'),
    !reason.trim() && adminText('closures.needs_reason'),
    !checked.trim() && adminText('closures.needs_checked'),
    typed.trim().toLowerCase() !== areaName.trim().toLowerCase() &&
      adminText('closures.needs_typed'),
  ].filter((x): x is string => Boolean(x));
  const close = async () => {
    const edited = preview?.may_edit_note && note !== preview.note_text ? { note_text: note } : {};
    const r = await cmd.run(
      'closure.close_area',
      { ...args(), ...edited, typed_area_name: typed.trim(), what_checked: checked.trim() },
      reason.trim(),
    );
    if (r?.ok)
      onDone(
        adminText('closures.done_close', { n: Number(r.courses_affected ?? 0), area: areaName }),
      );
  };
  // a change to the zone, the words or the kind needs a new preview
  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      setPreview(null);
      set(v);
    };
  return (
    <form
      className="card"
      aria-labelledby="new-closure-h"
      onSubmit={(e) => {
        e.preventDefault();
        void close();
      }}
    >
      <h3 id="new-closure-h">{adminText('closures.new_heading')}</h3>
      {data.role === 'backup_contact' && (
        <p className="note">{adminText('closures.backup_note')}</p>
      )}
      <fieldset>
        <legend>{adminText('closures.zone_legend')}</legend>
        {(['areas', 'circle', 'polygon'] as const).map((k) => (
          <label className="check" key={k}>
            <input
              type="radio"
              name="zone"
              checked={kind === k}
              onChange={() => reset(setKind)(k)}
            />
            {adminText(`closures.zone.${k}`)}
          </label>
        ))}
      </fieldset>
      {kind === 'areas' &&
        (data.areas.length === 0 ? (
          <p className="muted">{adminText('closures.no_areas')}</p>
        ) : (
          <fieldset>
            <legend>{adminText('closures.areas_legend')}</legend>
            {data.areas.map((a) => (
              <label className="check" key={a.code}>
                <input
                  type="checkbox"
                  checked={areas.includes(a.code)}
                  onChange={(e) =>
                    reset(setAreas)(
                      e.target.checked ? [...areas, a.code] : areas.filter((x) => x !== a.code),
                    )
                  }
                />
                {a.code}
              </label>
            ))}
          </fieldset>
        ))}
      {kind === 'circle' && (
        <div>
          <label htmlFor="c-lat">{adminText('closures.lat')}</label>
          <input
            id="c-lat"
            inputMode="decimal"
            value={lat}
            onChange={(e) => reset(setLat)(e.target.value)}
          />
          <label htmlFor="c-lon">{adminText('closures.lon')}</label>
          <input
            id="c-lon"
            inputMode="decimal"
            value={lon}
            onChange={(e) => reset(setLon)(e.target.value)}
          />
          <label htmlFor="c-r">{adminText('closures.radius')}</label>
          <input
            id="c-r"
            inputMode="decimal"
            value={radiusKm}
            onChange={(e) => reset(setRadiusKm)(e.target.value)}
          />
        </div>
      )}
      {kind === 'polygon' && (
        <>
          <label htmlFor="c-poly">{adminText('closures.polygon')}</label>
          <textarea
            id="c-poly"
            rows={4}
            value={geojson}
            onChange={(e) => reset(setGeojson)(e.target.value)}
          />
        </>
      )}
      <label htmlFor="c-area">{adminText('closures.area_name')}</label>
      <input id="c-area" value={areaName} onChange={(e) => reset(setAreaName)(e.target.value)} />
      <label className="check">
        <input type="checkbox" checked={part} onChange={(e) => reset(setPart)(e.target.checked)} />
        {adminText('closures.part_of_area')}
      </label>
      <label htmlFor="c-kind">{adminText('closures.kind_label')}</label>
      <select
        id="c-kind"
        value={eventKind}
        onChange={(e) => reset(setEventKind)(e.target.value as EventKind | '')}
      >
        <option value="">{adminText('closures.kind_pick')}</option>
        {EVENT_KINDS.map((k) => (
          <option key={k} value={k}>
            {adminText(`closures.kind.${k}`)}
          </option>
        ))}
      </select>
      {eventKind === 'hurricane' && (
        <>
          <label htmlFor="c-tropical">{adminText('closures.tropical_label')}</label>
          <select
            id="c-tropical"
            value={tropical}
            onChange={(e) => reset(setTropical)(e.target.value)}
          >
            <option value="">{adminText('closures.tropical_auto')}</option>
            {['hurricane', 'typhoon', 'cyclone'].map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </>
      )}
      <label htmlFor="c-template">{adminText('closures.template_label')}</label>
      <select id="c-template" value={template} onChange={(e) => reset(setTemplate)(e.target.value)}>
        {data.templates.map((t) => (
          <option key={`${t.key}@${t.version}`} value={`${t.key}@${t.version}`}>
            {adminText('closures.template_option', { key: t.key, version: t.version })}
          </option>
        ))}
      </select>
      <p>
        <button type="button" onClick={() => void look()}>
          {adminText('closures.preview')}
        </button>
      </p>
      {preview && !preview.ok && (
        <p className="warn" role="status">
          {closureMessage({ ok: false, error: preview.error ?? '' })}
        </p>
      )}
      {preview?.ok && (
        <section aria-labelledby="preview-h" className="preview">
          <h4 id="preview-h">{adminText('closures.preview_heading')}</h4>
          <p>{adminText('closures.preview_courses', { n: preview.courses_count ?? 0 })}</p>
          <ul className="names">
            {(preview.courses ?? []).map((c) => (
              <li key={c.course_id}>{c.name}</li>
            ))}
          </ul>
          <p>
            {(preview.clubs ?? []).length === 0
              ? adminText('closures.preview_no_clubs')
              : adminText('closures.preview_clubs', { names: (preview.clubs ?? []).join(', ') })}
          </p>
          <p className="muted">{adminText('closures.preview_later')}</p>
          <h4>{adminText('closures.note_heading')}</h4>
          {preview.may_edit_note ? (
            <>
              <label htmlFor="c-note">{adminText('closures.note_edit')}</label>
              <textarea
                id="c-note"
                rows={7}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <p className="muted">{adminText('closures.note_rule')}</p>
            </>
          ) : (
            <p className="note-text">{preview.note_text}</p>
          )}
          <p>{adminText('closures.note_u18_heading')}</p>
          <p className="note-text">{preview.note_text_under18}</p>
          {(preview.note_problems ?? []).length > 0 && (
            <ul className="warn">
              {(preview.note_problems ?? []).map((p) => (
                <li key={p}>{adminText(`closures.problem.${p}`)}</li>
              ))}
            </ul>
          )}
        </section>
      )}
      <label htmlFor="c-reason">{adminText('closures.reason')}</label>
      <input id="c-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      <label htmlFor="c-checked">{adminText('closures.checked')}</label>
      <textarea
        id="c-checked"
        rows={2}
        value={checked}
        onChange={(e) => setChecked(e.target.value)}
      />
      <label htmlFor="c-typed">{adminText('closures.typed', { area: areaName || '…' })}</label>
      <input id="c-typed" value={typed} onChange={(e) => setTyped(e.target.value)} />
      {cmd.needCode && <CodeField id="c-code" code={cmd.code} setCode={cmd.setCode} />}
      {missing.length > 0 && (
        <div className="muted" id="c-missing">
          <p>{adminText('closures.needs_heading')}</p>
          <ul>
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      <p>
        <button
          className="primary danger"
          type="submit"
          disabled={missing.length > 0}
          aria-describedby={missing.length > 0 ? 'c-missing' : undefined}
        >
          {adminText('closures.close_button', { n: preview?.courses_count ?? 0 })}
        </button>
      </p>
      {cmd.msg && (
        <p role="status" className="warn">
          {cmd.msg}
        </p>
      )}
    </form>
  );
}

function ClosureCard({
  sb,
  c,
  owner,
  lastActive,
  onDone,
}: {
  sb: SupabaseClient;
  c: Closure;
  owner: boolean;
  lastActive: boolean;
  onDone: (text: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [pauseOff, setPauseOff] = useState(false);
  const cmd = useCommand(sb);
  const courses = c.courses ?? [];
  const closed = courses.filter((x) => x.reopened_at === null);
  const act = async (command: string, args: Record<string, unknown>, done: string) => {
    if (!reason.trim()) return cmd.setMsg(adminText('closures.needs_reason'));
    const r = await cmd.run(command, { closure_id: c.id, ...args }, reason.trim());
    if (r?.ok) onDone(done);
  };
  return (
    <article className="card" aria-labelledby={`cl-${c.id}`}>
      <div className="title-row">
        <h3 id={`cl-${c.id}`}>{c.area_name}</h3>
        <span className="chips">
          <span className="chip">{adminText(`closures.kind.${c.event_kind}`)}</span>
          <span className="chip">{adminText(`closures.status.${c.status}`)}</span>
        </span>
      </div>
      {c.closed_by_backup && <p className="warn">{adminText('closures.by_backup')}</p>}
      <p className="muted">
        {c.status === 'active'
          ? adminText('closures.since', { at: at(c.started_at), review: at(c.review_due_at) })
          : adminText('closures.lifted', { from: at(c.started_at), to: at(c.lifted_at) })}
      </p>
      {c.review_overdue ? (
        <p className="warn">{adminText('closures.overdue')}</p>
      ) : (
        c.review_due && <p className="note">{adminText('closures.due')}</p>
      )}
      {c.status === 'active' && (
        <p>{adminText('closures.course_count', { closed: closed.length, all: courses.length })}</p>
      )}
      <p className="note-text">{c.note_text}</p>
      <details>
        <summary>{adminText('closures.details')}</summary>
        <p>{adminText('closures.reason_was', { reason: c.internal_reason })}</p>
        <p>{adminText('closures.checked_was', { checked: c.what_checked })}</p>
        <p className="muted">
          {adminText('closures.template_was', {
            key: c.note_template_key,
            version: c.note_template_version,
          })}
        </p>
        <ul className="plain courses">
          {courses.map((x) => {
            const where = x.reopened_at
              ? adminText('closures.reopened_at', { at: at(x.reopened_at) })
              : x.host_club
                ? adminText('closures.host', { club: x.host_club })
                : adminText('closures.no_host');
            return (
              <li key={x.course_id}>
                {owner && c.status === 'active' && x.reopened_at === null ? (
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={picked.includes(x.course_id)}
                      onChange={(e) =>
                        setPicked(
                          e.target.checked
                            ? [...picked, x.course_id]
                            : picked.filter((p) => p !== x.course_id),
                        )
                      }
                    />
                    <span>
                      {x.name} <span className="muted">({where})</span>
                    </span>
                  </label>
                ) : (
                  <>
                    {x.name} <span className="muted">({where})</span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        <h4>{adminText('closures.history')}</h4>
        <ol>
          {(c.history ?? []).map((h, i) => (
            <li key={i}>
              {adminText('closures.history_line', {
                at: at(h.at),
                what: adminText(`closures.command.${h.command.replace(/^closure\./, '')}`),
                reason: h.reason ?? '',
              })}
            </li>
          ))}
        </ol>
      </details>
      {owner && c.status === 'active' && (
        <>
          <label htmlFor={`rr-${c.id}`}>{adminText('closures.reason')}</label>
          <input id={`rr-${c.id}`} value={reason} onChange={(e) => setReason(e.target.value)} />
          {lastActive && (
            <label className="check">
              <input
                type="checkbox"
                checked={pauseOff}
                onChange={(e) => setPauseOff(e.target.checked)}
              />
              {adminText('closures.pause_off')}
            </label>
          )}
          {cmd.needCode && <CodeField id={`rc-${c.id}`} code={cmd.code} setCode={cmd.setCode} />}
          <div className="actions">
            <button
              type="button"
              onClick={() =>
                void act(
                  'closure.review',
                  {},
                  adminText('closures.done_review', { area: c.area_name }),
                )
              }
            >
              {adminText('closures.still_closed')}
            </button>
            <button
              type="button"
              onClick={() =>
                void act(
                  'closure.reopen',
                  { sensitivity_pause_off: pauseOff },
                  adminText('closures.done_reopen', { area: c.area_name }),
                )
              }
            >
              {adminText('closures.reopen_all')}
            </button>
            {picked.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  void act(
                    'closure.reopen',
                    { course_ids: picked, sensitivity_pause_off: pauseOff },
                    adminText('closures.done_reopen_some', { n: picked.length }),
                  )
                }
              >
                {adminText('closures.reopen_some', { n: picked.length })}
              </button>
            )}
          </div>
        </>
      )}
      {cmd.msg && (
        <p role="status" className="warn">
          {cmd.msg}
        </p>
      )}
    </article>
  );
}

export function ClosuresPage({ sb }: { sb: SupabaseClient }) {
  const [data, setData] = useState<ClosuresData | string | undefined>(undefined);
  const [done, setDone] = useState('');
  const load = () => {
    void loadClosures(sb).then(setData);
  };
  useEffect(load, [sb]);
  const after = (text: string) => {
    setDone(text);
    load();
  };
  const active = typeof data === 'object' ? data.closures.filter((c) => c.status === 'active') : [];
  const past = typeof data === 'object' ? data.closures.filter((c) => c.status === 'lifted') : [];
  return (
    <section aria-labelledby="closures-h">
      <h2 id="closures-h">{adminText('closures.heading')}</h2>
      <p className="muted">{adminText('closures.intro')}</p>
      {done && <p role="status">{done}</p>}
      {data === undefined && (
        <p className="muted" role="status">
          {adminText('closures.loading')}
        </p>
      )}
      {typeof data === 'string' &&
        (data === 'forbidden' ? (
          <p className="warn" role="alert">
            {adminText('closures.not_yours')}
          </p>
        ) : (
          <>
            <p className="warn" role="alert">
              {adminText('closures.error_load')}
            </p>
            <p>
              <button type="button" onClick={load}>
                {adminText('config.try_again')}
              </button>
            </p>
          </>
        ))}
      {typeof data === 'object' && (
        <>
          <p className={data.sensitivity_pause ? 'note' : 'muted'}>
            {adminText(data.sensitivity_pause ? 'closures.pause_on' : 'closures.pause_off_now')}
          </p>
          <h3>{adminText('closures.active_heading', { n: active.length })}</h3>
          {active.length === 0 && <p>{adminText('closures.none_active')}</p>}
          {active.map((c) => (
            <ClosureCard
              key={c.id}
              sb={sb}
              c={c}
              owner={data.role === 'owner'}
              lastActive={active.length === 1}
              onDone={after}
            />
          ))}
          <NewClosure sb={sb} data={data} onDone={after} />
          {past.length > 0 && (
            <>
              <h3>{adminText('closures.past_heading', { n: past.length })}</h3>
              {past.map((c) => (
                <ClosureCard
                  key={c.id}
                  sb={sb}
                  c={c}
                  owner={false}
                  lastActive={false}
                  onDone={after}
                />
              ))}
            </>
          )}
        </>
      )}
    </section>
  );
}
